//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import Radium from "radium";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-scheduler-suggested-time imports
import { IContainer, IProps, IState } from "./EventSchedulerSuggestedTime.d";
import { eventSchedulerSuggestedTimeFragments } from "./EventSchedulerSuggestedTime.gql";
import * as s from "./EventSchedulerSuggestedTime.styles";
// state
import { IReduxState } from "@reducers/root.reducer";
// util
import { ISlotInfo, getMomentForSlot, processSlot } from "@util/scheduling-slot.util";
import { PostMessageManager } from "@util/post-message.util";
// components
import { AttendeesPainsRenderer } from "@components/attendees-pains-renderer";
// material-ui
import { Tooltip } from "@material-ui/core";
import { Business, CheckCircle } from "@material-ui/icons";
import { track, TrackingEvents, TrackingHoverDelay } from "@util/analytics.util";
import { isOnToolTipDebugging } from "@util/experiment.util";

//////////////////
// COMPONENT
//////////////////
@Radium
class EventSchedulerSuggestedTimeCmpt extends React.Component<IProps, IState> {
  private hoverTimer: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      hovered: false,
      highlight: !!props.highlight,
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.highlight !== this.state.highlight) {
      this.setState({ highlight: !!nextProps.highlight });
    }
  }

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onClickSlot = (slot: ISchema.ISchedulingSlot) => {
    const { reschedulerEvent } = this.props;
    this.setState({ highlight: true });
    if (!reschedulerEvent) {
      this.onHoverSlot(slot, false, this.props.duration); // post-message to remove slot highlighting in gcal
    }
    if (this.props.onClick) {
      this.props.onClick(slot);
    }
  };

  private onHoverSlot = (slot: ISchema.ISchedulingSlot, hovered: boolean, duration: number) => {
    const startMillis = getMomentForSlot(slot).valueOf();
    PostMessageManager.sendShowEventOnCalendar(startMillis, duration, hovered);
    this.setState({ hovered });

    // maybe track
    if (hovered) {
      this.hoverTimer = setTimeout(() => {
        track(TrackingEvents.SCHEDULING.HOVERED_SUGGESTED_TIME);
      }, TrackingHoverDelay);
    }
    if (!hovered && this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderTags = (slotInfo: ISlotInfo) => {
    const tagStyle = slotInfo.isBest ? s.best : {};
    return (
      <div style={s.tags}>
        <span style={tagStyle}>{slotInfo.tagText}</span>
      </div>
    );
  };

  private maybeRenderRoomAvailability = (deltaInfo: ISlotInfo) => {
    if (!this.props.includeRoom) {
      return <span />;
    }

    const roomCount = deltaInfo.rooms.length;
    const roomString =
      roomCount === 0
        ? "No rooms available"
        : `${roomCount} room${roomCount > 1 ? "s" : ""} available`;

    const containerStyle = Object.assign(
      {},
      s.room,
      roomCount === 0 ? { color: s.warning.color } : {},
    );
    const icon = Object.assign(
      {},
      s.businessIcon,
      roomCount === 0 ? { htmlColor: s.warning.color } : {},
    );

    return (
      <div style={containerStyle}>
        <Business {...icon} />
        <span style={{ marginTop: 1 }}>{roomString}</span>
      </div>
    );
  };

  private renderScore = (slotInfo: ISlotInfo) => {
    return (
      <div style={s.score}>
        <span style={s.stat}>{slotInfo.scoreText}</span>{" "}
        {slotInfo.score > 0 ? slotInfo.scoreSuffix : "on Focus Time"}
      </div>
    );
  };

  public render() {
    const { schedulingSlot, duration, persons, qualifiers } = this.props;
    const info = processSlot(schedulingSlot, qualifiers);

    if (!info || !schedulingSlot) {
      return <span />;
    }

    const containerStyle = this.state.hovered
      ? s.suggestionHovered
      : this.state.highlight
      ? s.suggestionHighlighted
      : s.suggestion;

    return (
      <Tooltip
        title={<div>{JSON.stringify(schedulingSlot)}</div>}
        open={isOnToolTipDebugging(this.props.org) ? undefined : false}
      >
        <div
          style={containerStyle}
          onClick={() => this.onClickSlot(schedulingSlot)}
          onMouseEnter={() => this.onHoverSlot(schedulingSlot, true, duration)}
          onMouseLeave={() => this.onHoverSlot(schedulingSlot, false, duration)}
        >
          {this.state.highlight ? <CheckCircle {...s.checkIcon} /> : ""}
          {this.renderTags(info)}
          <div style={s.date}>{info.dateText}</div>
          {this.renderScore(info)}
          {this.maybeRenderRoomAvailability(info)}
          <AttendeesPainsRenderer schedulingSlot={schedulingSlot} persons={persons} />
        </div>
      </Tooltip>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    reschedulerEvent: state.feedScheduler.reschedulerEvent,
  };
}

const EventSchedulerSuggestedTimeRedux = connect(
  mapStateToProps,
  {},
)(EventSchedulerSuggestedTimeCmpt);

//////////////////
// RELAY
//////////////////
export const EventSchedulerSuggestedTime = createFragmentContainer<IContainer>(
  EventSchedulerSuggestedTimeRedux,
  eventSchedulerSuggestedTimeFragments,
);
